import {TKey, useTranslation} from '@/translations/i18n'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useForm, Controller} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {ClinicalPresentationFormSchema} from '../../formSchemas'
import {ProjectExtended} from '../../types'
import {useOutletContext} from 'react-router-dom'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import {RadioSteps} from '@/components/commons/radio-steps/RadioSteps'
import {clinicalPresentationToLabel, stabilityLevelToLabel} from '@/features/projects/utils'
import {ObjectEntries} from '@/utilities/helpers'
import {StyledClinicalPresentationCardFormFieldGrid} from './style'
import {Label} from '@/components/ui/label/Label'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {CareNeedsMultiSelect} from '../care-need-multiselect/CareNeedsMultiselect'
import {useStoreClinicalPresentations} from '../../queries/useStoreClinicalPresentations'
import {useEditClinicalPresentations} from '../../queries/useEditClinicalPresentations'

interface ClinicalPresentationCardFormProps {
    onCancel: () => void
    defaultValues?: Partial<ClinicalPresentationFormSchema>
    isEdit?: boolean
}

const PROJECT_CLINICAL_PRESENTATION_FIELDS = [
    {
        label: 'projects:cognitive_disorders',
        name: 'cognitiveDisorders'
    },
    {
        label: 'projects:behavioral_disorders',
        name: 'behavioralDisorders'
    },
    {
        label: 'projects:communication_disorders',
        name: 'communicationDisorders'
    },
    {
        label: 'projects:sensory_deficits',
        name: 'sensoryDeficit'
    }
] as const satisfies {label: TKey; name: keyof ClinicalPresentationFormSchema}[]

export const ClinicalPresentationCardForm = ({onCancel, defaultValues, isEdit}: ClinicalPresentationCardFormProps) => {
    const {t} = useTranslation()
    const {project} = useOutletContext<{project: ProjectExtended}>()
    const queryClient = useQueryClient()
    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_CLINICAL_PRESENTATIONS, project.id]})
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, project.id]})
        onCancel()
    }
    const {mutate: storeClinicalPresentations, isPending: isLoadingStoreClinicalPresentations} =
        useStoreClinicalPresentations(
            {
                projectId: project.id
            },
            {onSuccess}
        )
    const {mutate: editClinicalPresentations, isPending: isLoadingEditClinicalPresentations} =
        useEditClinicalPresentations(
            {
                projectId: project.id
            },
            {onSuccess}
        )

    const {
        control,
        handleSubmit,
        formState: {errors, isValid, isDirty}
    } = useForm<ClinicalPresentationFormSchema>({
        mode: 'onBlur',
        resolver: zodResolver(ClinicalPresentationFormSchema),
        defaultValues: defaultValues
    })

    const onSubmit = handleSubmit(data => {
        if (isEdit) {
            editClinicalPresentations(data)
        } else {
            storeClinicalPresentations(data)
        }
    })

    return (
        <DetailsFieldsSection
            variant="neutral"
            title={t('projects:social_welfare_conditions')}
            headingEndSlot={
                <EditDetailsSectionButtons
                    isActive={true}
                    onCancel={onCancel}
                    onConfirm={onSubmit}
                    isDisabled={!isValid || !isDirty}
                    isLoading={isLoadingStoreClinicalPresentations || isLoadingEditClinicalPresentations}
                />
            }
        >
            <Flexbox direction="column" gap={5} fullWidth>
                <Flexbox direction="column" gap={7} fullWidth>
                    {PROJECT_CLINICAL_PRESENTATION_FIELDS.map(field => (
                        <StyledClinicalPresentationCardFormFieldGrid key={field.name}>
                            <Label>{t(field.label)}</Label>
                            <Controller
                                control={control}
                                name={field.name}
                                render={({field}) => (
                                    <RadioSteps
                                        steps={ObjectEntries(clinicalPresentationToLabel).map(
                                            ([key, translationKey]) => ({
                                                label: t(translationKey),
                                                value: key
                                            })
                                        )}
                                        onChange={field.onChange}
                                        value={field.value}
                                        name={field.name}
                                        size="sm"
                                        itemWidth="160px"
                                    />
                                )}
                            />
                        </StyledClinicalPresentationCardFormFieldGrid>
                    ))}
                    <Flexbox direction="column" gap={4} style={{width: '50%'}}>
                        <Controller
                            control={control}
                            name="stabilityLevel"
                            render={({field, fieldState}) => (
                                <InputSelect
                                    label={t('projects:stability_level')}
                                    optionsMap={stabilityLevelToLabel}
                                    onChange={field.onChange}
                                    value={field.value}
                                    errorMessage={fieldState.error?.message}
                                />
                            )}
                        />

                        <Controller
                            control={control}
                            name="careNeeds"
                            render={({field}) => (
                                <CareNeedsMultiSelect
                                    values={field?.value ?? []}
                                    errorMessage={errors.careNeeds?.message}
                                    onChange={value => {
                                        if (field.value?.some(careNeed => careNeed.name === value.name)) {
                                            field.onChange(
                                                field.value?.filter(careNeed => careNeed.name !== value.name)
                                            )
                                        } else {
                                            field.onChange(field.value?.concat(value) || [value])
                                        }
                                    }}
                                />
                            )}
                        />
                    </Flexbox>
                </Flexbox>
            </Flexbox>
        </DetailsFieldsSection>
    )
}
